import * as THREE from 'three';
import cuid from 'cuid';
import uuid from 'node-uuid';
import { Map, List, fromJS } from 'immutable';
import { contains, forEach, map, mapObjIndexed, curry, pick, reduce } from 'ramda';
import createReducer from './utils/createReducer';
import { addFile } from './assets';
//import { fetchmaterial } from './files';
import { ADD_NODE, ADD_VR_CAMERA, DELETE_NODE, LOAD_SCENE, LOADED_FILE, SET_VR_CAMERA, CLEAR_SCENE } from './actionTypes';
import operators, { lookupOperator } from '../operators/nonImmutable';
import { evaluateSceneGraph } from './utils/evalSceneGraph';
import { ActionTypes } from '../store/createStore';

import { getWorldTransform } from './scene';

const SET = 'SET';
const MERGE = 'MERGE';
const REPLACE = 'REPLACE';
const MULTI = 'MULTI';
const REPARENT = 'REPARENT';
const ADD_OPERATOR = 'ADD_OPERATOR';
const TOUCH = 'TOUCH';
const virtualCameraId = 'cccccccc-cccc-1ccc-accc-cccccccccccc';
const vrCameraId = 'bbbbbbbb-bbbb-0bbb-zbbb-bbbbbbbbbbbb';
const virtualTargetId = 'dddddddd-dddd-0ddd-addd-dddddddddddd';

/*
 * Convert from immutable.js scenegraph to our new object format
 */
function convertFromImmutable(scene) {
  return scene.map((node) => {
    const warnings = node.get('warnings').toArray();
    const plugs = node.get('plugs').map((ops, plug) => {
      return ops.map((op) => {
        let type = op.get('type');
        let operator = lookupOperator(plug, type, warnings);
        const opjs = operator.init(type, op.toJS(), op.get('name'));
        //console.log('init operator: ', plug, opjs);
        return opjs;
      }).toArray();
    }).toObject();
    var nodeJS = node.toJS();
    nodeJS.plugs = plugs;
    nodeJS.warnings = warnings;
    nodeJS._v = 0;
    return nodeJS;
  }).toObject();
}

/**
 * Initialize node from object literal format
 */
export function initNode(node) {
  const plugs = {};
  const warnings = node.warnings || [];
  Object.keys(node.plugs).forEach((plugName) => {
    plugs[plugName] = node.plugs[plugName].map((op) => {
      const type = op.type;
      const operator = lookupOperator(plugName, type, warnings);
      return operator.init(type, op, op.name);
    });
  });
  return Object.assign({}, node, { warnings, plugs, _v: 0 });
}

/*
 * Read and initialize nodes from object literal format
 */
function initNodes(nodes) {
  const result = {};
  Object.keys(nodes).forEach((id) => {
    result[id] = initNode(nodes[id]);
  });
  return result;
}

/**
 * This module contains actions to manipulate the scene graph.
 *
 * @module sceneGraph
 * @public
 *
 */

/*
 * Reducer
 */
function reducer(records) {
  function createOperator(OperatorRecord, type, props, name) {
    if (!name) name = type;

    let Op = new OperatorRecord({ type, name });
    // Calling merge directly so that the immutable shim has its `set` method called
    // Filtering the operator to only known keys.
    return Op.merge(pick(OperatorRecord.keys(), props));
  };

  function checkNode(node) {
    if (!node.hasOwnProperty('_v')) node._v = 0;
    return node;
  }

  function buildNode({ id, name, type, children, parent, plugs }) {
    let warnings = [];

    const initPlugs = mapObjIndexed((ops, plug) => {
      if (!records[plug]) {
        warnings = warnings.push(`No Plug: ${plug}`);
        return [];
      }

      return ops.map((op) => {
        if (!Array.isArray(op)) throw new Error('Expect op to be an array of 2 or 3');

        let [type, props, name] = op;
        const operator = lookupOperator(plug, type, warnings);
        var opdata = operator.init(type, props, name); // Object.assign({}, props, { type, name });
        //console.log('init operator: ', type, props, opdata);
        return opdata;
        //let OperatorRecord = records[plug][type];

        //if (!OperatorRecord) {
        //  warnings = warnings.push(`No Operator: ${plug} - ${type}`);
        //  //console.log('WARNING:', warnings.toJS());
        //  OperatorRecord = records.None.NoOp;
        //}
        //return createOperator(OperatorRecord, type, props, name);
      });
    }, plugs);

    const newChildren = children || [];

    return {
      id,
      name,
      type,
      children: newChildren,
      parent,
      plugs: initPlugs,
      warnings,
      _v: 0,
    };
  };

  function errorNode({ id, name, children, parent }) {
    children = children ? List(children) : List();
    return Map({ id, name, type: 'ErrorNode', children, parent, plugs: Map() });
  };

  function initializeVirtualNodes(state, options={}) {
    if (!state.nodes[virtualCameraId] || options.force) {
      state.nodes[virtualCameraId] = buildNode({
        id: virtualCameraId,
        name: 'CAMERA',
        type: 'Camera',
        plugs: {
          Camera: [['Camera', {}]],
          Transform: [['Transform', {}]],
          Properties: [['Default', {}]],
        },
      });
    }

    if (!state.nodes[vrCameraId] || options.force) {
      state.nodes[vrCameraId] = buildNode({
        id: vrCameraId,
        name: 'VR CAMERA',
        type: 'Camera',
        plugs: {
          Camera: [['Camera', { fieldOfView: 45 }]],
          Transform: [['Transform', { translation: { x: 0, y: 1.6, z: 1 } }]],
          Properties: [['Default', {}]],
        },
      });
    }

    if (!state.nodes[virtualTargetId] || options.force) {
      state.nodes[virtualTargetId] = buildNode({
        id: virtualTargetId,
        name: 'VIRTUAL TARGET NULL',
        type: 'Null',
        plugs: {
          Transform: [['Transform', {}]],
        },
      });
    }
  }

  function newInitialState() {
    return {
      _v: 0,
      nodes: {},
      fileReferences: { to: {}, from: {} },
      nodeReferences: { to: {}, from: {} },
      evaluatedNodes: {},
      loaded: false,
      _ev: 0,
    };
  }

  function setReducer(state, { path, value }) {
    if (!path.length || path.length < 2) {
      throw new Error('SET requires a path with length 2 or 4');
    }

    const [nodeId, plugName, idx, key] = path;
    const node = state.nodes[nodeId];

    if (path.length === 2 && path[1] === 'name') {
      node.name = value;
      node._v += 1;
      state._v += 1;
    } else {
      const plug = node && node.plugs[plugName];
      const op = plug && plug[idx];
      if (!op) throw new Error('Setting data on unknown operator: ' + path.join(','));

      const operator = operators[plugName][op.type];
      if (operator.set(op, key, value)) {
        node._v += 1;
        state._v += 1;
      };
    }

    return state;
  }

  const sceneReducer = createReducer({}, {
    // If we have multiple players on a page, the state object for the sceneGraph module must be unique.
    // Use the @@redux/INIT action to create a new base state object.
    [ActionTypes.INIT](state) {
      return newInitialState();
    },

    [TOUCH](state, id) {
      const node = state.nodes[id];
      if (node) {
        node._v += 1;
        state._v += 1;
      }

      return state;
    },

    [SET]: setReducer,

    [MERGE](state, { path, attrs }) {
      if (!path.length || path.length !== 3) {
        throw new Error('MERGE requires a path with length 3');
      }

      if (typeof attrs !== 'object') {
        throw new Error('MERGE requires an object to merge in');
      }

      const [nodeId, plugName, idx] = path;
      const node = state.nodes[nodeId];
      const plug = node && node.plugs[plugName];
      const op = plug && plug[idx];
      const operator = op && operators[plugName][op.type];
      if (!operator) throw new Error('Setting data on unknown operator: ' + path.join(','));
      for (var k of Object.keys(attrs)) {
        operator.set(op, k, attrs[k]);
      }
      //op._v += 1;
      node._v += 1;
      state._v += 1;
      return state;
    },

    [REPLACE](state, { id, node }) {
      const originalNode = state.nodes[id];
      if (!originalNode) return;

      const v = originalNode._v;
      state.nodes[id] = node;
      node._v = v + 1;
      state._v += 1;
      return state;
    },

    [ADD_NODE](state, attrs) {
      const { id, parent } = attrs;
      const parentNode = parent && state.nodes[parent];
      if (parent && !parentNode) throw new Error(`Unknown parent: '${parent}'`);

      if (state.nodes[id]) throw new Error(`Adding a node that already exists: ${id}`);

      state.nodes[id] = attrs.node ? checkNode(attrs.node) : buildNode(attrs);

      if (parent && !parentNode.children) {
        parentNode.children = [];
      }

      if (parent && parentNode.children.indexOf(id) === -1) {
        parentNode.children.push(id);
      }

      state._v += 1;
      return state;
    },

    [DELETE_NODE](state, nodeId) {
      function deleteNodesFromState(id) {
        const node = state.nodes[id];
        if (node) {
          for (let childId of (node.children || [])) {
            deleteNodesFromState(childId);
          }

          const references = state.nodeReferences.from[id];
          Object.keys(references || {}).forEach((refKey) => {
            const path = JSON.parse(refKey);
            setReducer(state, { path, value: null });
          });
          delete state.nodes[id];
        }

        return node;
      }

      const node = deleteNodesFromState(nodeId);
      if (!node) return state;
      let parent = node.parent && state.nodes[node.parent];
      if (parent) {
        let idx = parent.children.indexOf(nodeId);
        if (idx !== -1) parent.children.splice(idx, 1);
        parent._v += 1;
      }

      state._v += 1;
      return state;
    },

    [ADD_OPERATOR](state, attrs) {
      const { id, plug, op } = attrs;
      const node = state.nodes[id];
      if (!node) throw new Error(`Unknown node: ${id}`);

      const ops = node.plugs[plug];
      if (!ops) throw new Error(`Unknown plug: ${plug}`);

      if (!Array.isArray(op)) throw new Error('Expect op to be an array of 2 or 3');

      const [type, props, name] = op;
      const operator = lookupOperator(plug, type, node.warnings);
      ops.push(operator.init(type, op, name));
      node._v += 1;
      state._v += 1;

      return state;
    },

    [MULTI](state, ops) {
      const v = state._v;
      for (let opId in ops) {
        let op = ops[opId];
        if (op.type === SET) setReducer(state, op.payload);
      }

      state._v = v + 1;
      //console.log('create new multi state in: ', new Date() - s);
      return state;
    },

    [REPARENT](state, { parentId, childIds }) {
      const newParentNode = state.nodes[parentId];
      if (!newParentNode) {
        console.warn(`Unknown parent for reparent: ${parentId}`);
        return state;
      }

      forEach((childId) => {
        const childNode = state.nodes[childId];
        if (!childNode) return;

        const childParentId = childNode && childNode.parent;
        const prevParentNode = childParentId && state.nodes[childParentId];
        let idx = prevParentNode && prevParentNode.children ? prevParentNode.children.indexOf(childId) : -1;
        if (idx !== -1) {
          prevParentNode.children.splice(idx, 1);
          prevParentNode._v += 1;
        }

        newParentNode.children.push(childId);
        childNode.parent = parentId;
        childNode._v += 1;
      }, childIds.slice()); // iterate copy of array to protect from corruption from child removals

      newParentNode._v += 1;
      state._v += 1;
      return state;
    },

    [LOAD_SCENE](state, { scene, nodes }) {
      const sceneJS = scene
        ? convertFromImmutable(scene)
        : initNodes(JSON.parse(nodes));

      Object.assign(state.nodes, sceneJS);

      initializeVirtualNodes(state);
      state._v += 1;

      return state;
      //const newState = state.merge(scene);
      //return newState.get(virtualCameraId) ? newState : newState.set(virtualCameraId, buildNode({
      //  id: virtualCameraId,
      //  name: 'CAMERA',
      //  type: 'Camera',
      //  plugs: {
      //    Camera: [['Camera', {}]],
      //    Transform: [['Transform', {}]],
      //    Properties: [['Default', {}]],
      //  },
      //}));
    },

    [CLEAR_SCENE](state, id) {
      initializeVirtualNodes(state, { force: true });
      state._v += 1;
      if (state.nodes[id]) state.nodes[id]._v = 0;
      return state;
    },

    [LOADED_FILE](state, id) {
      if (state.fileReferences.from[id]) {
        for (let refKey of Object.keys(state.fileReferences.from[id])) {
          const [nodeId, plugName, opIdx] = JSON.parse(refKey);
          state.nodes[nodeId]._v += 1;
        }

        state._v += 1;
      }

      return state;
    },

    [SET_VR_CAMERA](state, cameraId) {
      // FIXME: remove this
      const camNode = state.get(cameraId);
      if (camNode.type === 'Camera') return state.set(vrCameraId, camNode);
      else return state;
    },

  });

  return sceneReducer;
}

// Return a new node object from existing node
function copyNode(node) {
  const newPlugs = {};
  for (let plug of Object.keys(node.plugs)) {
    newPlugs[plug] = map((op) => Object.assign({}, op), node.plugs[plug]);
  }

  return {
    id: uuid.v4(),
    name: node.name,
    type: node.type,
    parent: node.parent,
    children: [].concat(node.children),
    plugs: newPlugs,
    _v: 1,
  };
}

/**
 * Add a node to the scene.
 *
 * @public
 * @param {Node} node
 * @returns {Promise}
 */
export function addNode(node)  {
  let { id, name, type, plugs, parent } = node;
  if (!id) id = uuid.v4(); //cuid();
  name = name || id;

  return {
    type: ADD_NODE,
    payload: { id, name, type, plugs, parent },
    resolve: id,
  };
};

export function setVRCamera(camId) {
  return { type: SET_VR_CAMERA, payload: camId };
}

/**
 * Deletes a node
 *
 * @public
 * @param {String} id The node id
 * @returns {Promise}
 */
export function deleteNode(id) {
  return {
    type: DELETE_NODE,
    payload: id,
  };
};

/**
 *
 * Add an operator to a plug of a node.
 */
export function addOperator(id, plug, op) {
  return {
    type: ADD_OPERATOR,
    payload: { id, plug, op },
  };
};

/**
 * get a property
 *
 * @param {Path} path to the operator property.
 * @returns {any} value
 *
 * @example
 * // scene.get([uuid
 */
export function get(store, path) {
  if (!Array.isArray(path)) throw new Error('Expect path to be an array');

  const sceneGraph = store.get('sceneGraph');
  let result = sceneGraph.nodes[path[0]];

  if (path.length >= 2) {
    if (path[1] === 'name' || path[1] === 'type') {
      return result && result[path[1]];
    }

    result = result && result.plugs[path[1]];
  }

  if (path.length >= 3) {
    result = result && result[path[2]];
  }

  if (path.length >= 4 && result) {
    const operator = lookupOperator(path[1], result.type, []);
    result = operator.get(result, path[3]);
  }

  return result;
};

export function getVRCamera(store) {
  return store.getIn(['sceneGraph', vrCameraId]);
}

/**
 * set a property on an operator. This takes a Path array,
 * and a value
 *
 * @param {Path} path includes the operator property.
 * @param {any} value
 * @returns {Promise}
 *
 */
export function set(path, value) {
  return { type: SET, payload: { path, value }, meta: { remote: true } };
};

/**
 * Merges an object into an operator.
 *
 * @param {Path} path to the operator.
 * @param {Object} attrs
 * @returns {Promise}
 *
 */
export function merge(path, attrs) {
  return { type: MERGE, payload: { path, attrs } };
};

/**
 * Replace a node with the given node contents
 * @private
 *
 */
export function replace(id, node) {
  return { type: REPLACE, payload: { id, node } };
};

/**
 * An array of scene actions.
 *
 * @param {Action[]} ops
 * @returns {Promise}
 */
export function multi(ops) {
  return { type: MULTI, payload: ops };
};

/**
 * Touch sceneGraph node, so it is re-evaluated and translated
 */
export function touch(id) {
  return { type: TOUCH, payload: id };
};

/**
 * Reparent nodes to the new parent.
 *
 * @public
 * @param {String} parent id of the new parent node
 * @param {Array} childIds list of ids to reparent
 *
 * @example
 * // reparent children of 'MyModel' to 'NewParentNull'
 * const parentId = scene.find({ name: 'NewParentNull' });
 * const nodesToReparent = scene.filter({ from: { name: 'MyModel' } });
 * sceneGraph.reparent(parentId, nodesToReparent);
 */
export function reparent(parentId, childIds) {
  return { type: REPARENT, payload: { parentId, childIds } };
};

/**
 * Return the parent id of the node.
 */
export function findParent(store, id) {
  const node = store.get('sceneGraph').nodes[id];
  return node && node.parent;
};

/**
 * Return the root parent id of the node.
 */
export function findRoot(store, id) {
  const node = store.get('sceneGraph').nodes[id];
  const parent = node && node.parent;
  return parent ? findRoot(store, parent) : id;
};

/**
 * Clone nodes. Given a list of ids, this will copy the node with a new id,
 * and update the parent and child of each node. By passing in a map of *old
 * parent id* to *new parent id*, the nodes can be cloned to a different location
 * in the sceneGraph (or from one to another).
 *
 * @public
 * @param {Array} ids list of ids to clone
 * @param {Object} nodeMap of {oldNodeId: newNodeId} Can be used for passing
 *                         new parents, or new depencies.
 * @param {Object} [options]
 * @param {Boolean} [options.cloneDependencies] Clone dependencies as well.
 *
 * @example
 * // clone children of 'MyModel' to 'NewParentNull'
 * const parentId = scene.find({ name: 'NewParentNull' });
 * const myModelId = scene.find({ name: 'MyModel' });
 * const nodesToClone = scene.filter({ from: { id: myModelId } });
 * sceneGraph.clone(nodesToClone, { [myModelId]: parentId });
 * // clone dependencies
 * sceneGraph.clone(nodesToClone, { [myModelId]: parentId }, {cloneDependencies: true});
 */
export function clone(ids, nodeMap={}, options={}) {
  const { cloneDependencies } = options;
  const nodes = reduce((acc, id) => {
    acc[id] = true; return acc;
  }, {}, ids);
  const assets = {};

  return (store) => {
    const sceneGraph = store.get('sceneGraph');

    // Find dependencies
    function checkDependency(id) {
      const node = sceneGraph.nodes[id];
      const name = node && node.name;
      if (!node || !node.plugs) return;

      for (let plug of Object.keys(node.plugs)) {
        const ops = node.plugs[plug];

        for (let op of ops) {
          const operator = operators[plug][op.type];
          if (operator) {
            for (let key of operator.referenceKeys) {
              const refId = op[key];
              if (refId && !nodes[refId] && !nodeMap[refId]) {
                nodes[refId] = true;
                checkDependency(refId);
              }
            }

            for (let key of operator.fileReferenceKeys) {
              const refId = op[key];
              if (refId && store.getIn(['assets', refId])) {
                assets[refId] = true;
              }
            }
          }
        }
      }
    }

    if (cloneDependencies) forEach(checkDependency, ids);

    // Generate new ids
    forEach((id) => nodes[id] = uuid.v4(), Object.keys(nodes));
    forEach((assetId) => assets[assetId] = uuid.v4(), Object.keys(assets));

    function updateDependencies(node) {
      var resultNode = node;
      for (let plug of Object.keys(node.plugs)) {
        const ops = node.plugs[plug];
        ops.forEach((op, opIdx) => {
          const operator = operators[plug][op.type];
          if (operator) {
            for (let key of operator.referenceKeys) {
              const refId = op[key];
              const newRef = refId && (nodes[refId] || nodeMap[refId]);
              if (newRef) resultNode.plugs[plug][opIdx][key] = newRef;
            }

            for (let key of operator.fileReferenceKeys) {
              const refId = op[key];
              if (refId && assets[refId]) {
                resultNode.plugs[plug][opIdx][key] = assets[refId];
              }
            };
          }
        });
      }

      return resultNode;
    }


    // Remap parents, children and dependencies
    forEach((id) => {
      const oldNode = sceneGraph.nodes[id];
      const newNode = copyNode(oldNode);
      newNode.id = nodes[id];

      const nodeParent = oldNode.parent;
      const parent = nodeParent && (nodes[nodeParent] || nodeMap[nodeParent] || nodeParent);
      newNode.parent = parent;

      newNode.children = [];
      for (let childId of oldNode.children) {
        if (nodes[childId]) newNode.children.push(nodes[childId]);
        else if (nodeMap[childId]) newNode.children.push(nodeMap[childId]);
      }

      store.dispatch({
        type: ADD_NODE,
        payload: {
          id: nodes[id],
          parent,
          node: updateDependencies(newNode),
        },
      });
    }, Object.keys(nodes));

    var firstNodeRootId = findRoot(store, nodes[Object.keys(nodes)[0]]);

    forEach((assetId) => {
      const asset = store.getIn(['assets', assetId]);
      if (asset) {
        store.dispatch(addFile(asset.set('id', assets[assetId]).set('sceneId', firstNodeRootId).toJS()));
      }
    }, Object.keys(assets));

    return Promise.resolve(nodes);
  };
};

export function oldNodeJSON(node) {
  const plugs = Object.keys(node.plugs).map(function (plugName) {
    return {
      type: plugName,
      operators: node.plugs[plugName].map((op) => {
        const operator = lookupOperator(plugName, op.type, []);
        return operator.export(op);
      }),
    };
  });

  return {
    _id: node.id,
    name: node.name,
    type: node.type,
    plugs,
    nodes: [],
  };
};

/**
 * Given a list of nodes, build and return the old scene graph format.
 */
export function exportJSON(store, nodes) {
  let rootNode;
  const nodeMap = {};
  const sceneGraph = store.get('sceneGraph');

  forEach((nodeId) => {
    const node = sceneGraph.nodes[nodeId];
    if (!node) return;

    const newNode = oldNodeJSON(node);
    nodeMap[newNode._id] = newNode;
    if (!rootNode) rootNode = newNode;

    if (node.parent && nodeMap[node.parent]) {
      nodeMap[node.parent].nodes.push(newNode);
    }
  }, nodes);

  return rootNode;
};

//export function importMaterial(id) {
//  return ({dispatch, getState}) => {
//    // let sceneId = getState().getIn(['scene','id']);
//    return dispatch(fetchMaterial(id)).then((mat) => {
//      return dispatch(addNode({...mat, plugs: {
//        Material: [['Material', {color: {r: 1, g: 0, b: 0}}]]
//      }, parent: 'materiallibrary'}));
//    }).catch((err) => {
//      // console.log('handle rejected promise?', err);
//      return false;
//      // throw err;
//    });
//  }
//}

export function updateNodeTransform(store, nodeId, newTransform) {
  var parentNodeId = findParent(store, nodeId);

  //const parent = store.getIn(['sceneGraph',parentNode.get('id'),'parent']);
  var parentWorldTransform = getWorldTransform(store, parentNodeId);
  //console.log( 'parentWorldTransform', parentWorldTransform );

  var position = new THREE.Vector3(), quaternion = new THREE.Quaternion(), scale = new THREE.Vector3();

  var tempTransform = new THREE.Matrix4();
  tempTransform.copy(newTransform);

  if (parentWorldTransform) {
    var invParentWorldTransform = new THREE.Matrix4();
    invParentWorldTransform.getInverse(parentWorldTransform, true);
    tempTransform.multiplyMatrices(invParentWorldTransform, tempTransform);
  }

  tempTransform.decompose(position, quaternion, scale);
  //console.log( 'position', JSON.stringify( position ) );
  //console.log( 'scale', JSON.stringify( scale ) );
  var euler = new THREE.Euler().setFromQuaternion(quaternion, 'ZYX');
  //console.log( 'euler', JSON.stringify( euler ) );


  //let translation = node.getIn(['plugs','Transform',0,'translation']);
  //let rotation = node.getIn(['plugs','Transform',0,'rotation']);
  store.dispatch(
    set([nodeId, 'Transform', 0, 'translation'],
      { x: position.x, y: position.y, z: position.z })
    );
  store.dispatch(
    set([nodeId, 'Transform', 0, 'rotation'],
      { x: THREE.Math.radToDeg(euler.x), y: THREE.Math.radToDeg(euler.y), z: THREE.Math.radToDeg(euler.z) })
    );
}

export function getEvalSceneGraph(store) {
  const sceneGraph = store.get('sceneGraph');

  if (sceneGraph._v === sceneGraph._ev) {
    return sceneGraph;
  }

  const sceneId = store.getIn(['sceneIO', 'id']); // getSceneId
  //console.log('updateSceneGraph', sceneGraph._ev, 'from', sceneGraph._v);
  evaluateSceneGraph(store, sceneId, sceneGraph);
  return sceneGraph;
};

const publicApi = {
  reducer,
  actions: { set, merge, multi, addNode, deleteNode, reparent, clone, setVRCamera, addOperator },
  selectors: { get, exportJSON, getVRCamera },
};
export default publicApi;

export {
  virtualCameraId as virtualCameraId,
  vrCameraId as vrCameraId,
  virtualTargetId as virtualTargetId,
};



// WEBPACK FOOTER //
// ./src/modules/sceneGraph.js